import config from '@root/config'
import { OPZ2_ICON_SVG } from '@/assets/icons/opz2Icon'

export interface UxSvgIcon {
    svg: string
}

export interface UxMdiIcon {
    mdi: string
}

export type UxIcon = string | UxSvgIcon | UxMdiIcon

export function isUxSvgIcon(icon: UxIcon | null | undefined): icon is UxSvgIcon {
    return Boolean(icon && typeof icon === 'object' && 'svg' in icon && typeof icon.svg === 'string' && icon.svg.trim())
}

export function isUxMdiIcon(icon: UxIcon | null | undefined): icon is UxMdiIcon {
    return Boolean(icon && typeof icon === 'object' && 'mdi' in icon && typeof icon.mdi === 'string' && icon.mdi.trim())
}

export type UxMotifType = 'bars' | 'rings' | 'dots' | 'slash'
export type UxPricePeriod = 'once' | 'month'

export interface UxCardSettings {
    name: {
        text: string
        color: string
    }
    description: {
        text: string
        color: string
    }
    icon: {
        text: string
        bcg: string
        textColor: string
    }
    borderColor: string
    bcg: string
    motif: {
        type: UxMotifType
        color: string
    }
    badge: {
        text: string
        bcg: string
        textColor: string
    }
    price: {
        price: string
        period: UxPricePeriod
        priceColor: string
        periodColor: string
    }
    btn: {
        text: string
        textColor: string
        bcgColor: string
    }
}

export interface UxPageHeaderBadge {
    text: string | null
    color?: string
    bcg?: string
}

export interface UxPageHeaderCard {
    icon?: UxIcon | null
    badge?: UxPageHeaderBadge | null
    tags?: string | null
    title?: string | null
    shortDescription?: string | null
    extraInfoHtml?: string | null
}

export interface UxPageAboutFact {
    title: string
    description: string
}

export interface UxPageAboutCard {
    description: string
    secondDescription?: string
    facts?: UxPageAboutFact[]
}

export interface UxPageSkillCard {
    icon: UxIcon
    title: string
    description: string
}

export interface UxPageOtherIntegrationRef {
    id: string
    price?: string | null
    rating?: string | null
}

export interface UxPagePricingTierRow {
    term: string
    price: string
    discount?: string | null
}

export interface UxPagePricingTiers {
    type: 'tiers'
    header?: string | null
    unit?: string | null
    flagLast?: boolean
    bestRowIndex?: number
    showBestFlag?: boolean
    rows: UxPagePricingTierRow[]
    note?: string | null
}

export interface UxPagePricingMatrixRow {
    term: string
    cells: string[]
}

export interface UxPagePricingMatrix {
    type: 'matrix'
    header?: string | null
    plans: string[]
    rows: UxPagePricingMatrixRow[]
}

export type UxPagePricing = UxPagePricingTiers | UxPagePricingMatrix

export interface UxPageConnectPartner {
    name: string
    logoName?: string | null
    subtitle?: string | null
}

export interface UxPageConnectCard {
    price?: string | null
    facts?: string[]
    dhOfficial?: boolean
    partner?: UxPageConnectPartner | null
    pricing?: UxPagePricing | null
}

export interface UxPageSettings {
    headerCard?: UxPageHeaderCard
    aboutCard?: UxPageAboutCard
    skillsCards?: UxPageSkillCard[]
    otherIntegrations?: UxPageOtherIntegrationRef[]
    otherIntegrationsTitle?: string | null
    connectCard?: UxPageConnectCard
}

export interface UxModuleSettings {
    id?: string
    card?: Partial<UxCardSettings>
    page?: UxPageSettings
}

export const uxSettings: Record<string, UxModuleSettings> = {
    //TODO: вынести настройки в админку
    crmSettings: {
        id: config.marketplaceModulesIds[config.buildType].crmSettings,
        card: {},
        page: {
            headerCard: {
                icon: "⚙️",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Мы превращаем CRM из хранилища телефонов в живой инструмент продаж и заботы.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "У большинства салонов система Data Heroes подключена, но используется как хранилище телефонов. Администраторы работают вручную, нет системных касаний, непонятно кого звать и с каким оффером. База теряется, клиенты уходят без возврата.",
                secondDescription: "Premium-внедрение CRM-маркетинга — индивидуальная проработка системы под ваш бизнес от начала до результата. Глубокая сегментация базы (30–100 сегментов), сценарии под каждый этап жизненного цикла клиента, персонализированные офферы и скрипты «по-человечески», работа с цикличностью, контроль качества и 14 дней сопровождения после запуска. Увеличим конверсию в запись минимум в 1,5 раза. Освободим до 16 часов администратора в неделю. Авторская методика Сергея Теймурова и Софьи Жильцовой (основатель Data Heroes).",
                facts: [
                    { title: "×1,5", description: "минимальный рост конверсии в повторную запись" },
                    { title: "16 ч/нед", description: "экономия времени администратора" },
                    { title: "30–100", description: "сегментов базы по поведению и циклу" },
                    { title: "14 дней", description: "сопровождение после запуска" },
                ],
            },
            skillsCards: [
                {
                    icon: "🗂️",
                    title: "Глубокая сегментация",
                    description: "30–100 сегментов по температуре, жизненному циклу, группам услуг и типам поведения: редкие визиты, отмены, потенциальные VIP и другие.",
                },
                {
                    icon: "🔄",
                    title: "Сценарии под каждый цикл",
                    description: "0 визитов — возврат не дошедших. 1 визит — касания и оффер на повтор. 2 визита — усиление регулярности. 3+ — поддержание цикличности.",
                },
                {
                    icon: "✍️",
                    title: "Скрипты по-человечески",
                    description: "Персонализированные офферы под каждый сегмент. Каждое касание — два этапа: автоматическое и ручное. Без шаблонной сухости.",
                },
                {
                    icon: "📅",
                    title: "Работа с цикличностью",
                    description: "Напоминания привязаны к дате последнего визита и жизненному циклу услуги. Система сама знает, когда позвать.",
                },
                {
                    icon: "⭐",
                    title: "Контроль качества",
                    description: "Каждой услуге — свой вопрос для получения честной и полезной обратной связи.",
                },
                {
                    icon: "🎓",
                    title: "Обучение и сопровождение",
                    description: "Индивидуальная рабочая сессия 1,5–2 ч, мини-обучение команды 30–45 мин и 14 дней поддержки после запуска.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].salesTraining,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Стоимость по типу бизнеса",
                    unit: "",
                    flagLast: false,
                    rows: [{ term: "Моностудия", price: "70 000 ₽" },
                        { term: "Салон красоты", price: "90 000 ₽" },
                        { term: "Салон полного цикла", price: "110 000 ₽" },
                        { term: "Клиника косметологии", price: "140 000 ₽" }],
                    note: "+20% за каждый доп. филиал · срок выполнения 21–30 рабочих дней",
                },
                facts: [
                    "Глубокая сегментация базы: 30–100 сегментов",
                    "Сценарии под 0 / 1 / 2 / 3+ визитов",
                    "Персонализированные офферы и скрипты под каждый сегмент",
                    "Работа с цикличностью и датой последнего визита",
                    "Доп. сценарии: отмены, абонементы, кросс-продажи, VIP, дни рождения, уход мастера",
                    "Контроль качества: индивидуальные вопросы под каждую услугу",
                    "Подключение чат-бота Data Heroes",
                    "Индивидуальная рабочая сессия (1,5–2 ч)",
                    "Мини-обучение команды (30–45 мин)",
                    "14 дней сопровождения после запуска",
                ],
                dhOfficial: true,
            },
        },
    },
    beautyAutopilot: {
        id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
        card: {},
        page: {
            headerCard: {
                icon: "🚀",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Администраторы сами заполняют расписание — без напоминаний, штрафов и микроконтроля.",
                extraInfoHtml: "от <strong>Beauty-Autopilot®</strong> · на Data Heroes",
            },
            aboutCard: {
                description: "Администратор получает оклад плюс процент — и у него нет причины делать больше минимума. Руководитель объясняет, контролирует, снова объясняет. Расписание не заполняется, выручка не растёт, а собственник застрял в операционке вместо того, чтобы развивать бизнес.",
                secondDescription: "Бьюти-Автопилот — система управления прибылью через администраторов. Каждый день сотрудник получает конкретные задачи с измеримым результатом, а его зарплата напрямую зависит от выполнения. В основе три слоя: мотивационная схема Фикс + Бонус KPI + Премия от выручки, библиотека из 30+ авто-задач под типовые точки роста и smart-дашборд с цветовой кодировкой. Внедряется за 1–2 дня по готовым инструкциям. Работает независимо от системы DH — но органично дополняет её, если вы уже на платформе. Авторы используют Бьюти-Автопилот в своей сети GOLOVA с 2019 года: рост выручки +30% ежегодно, ФОТ снижен с 40% до 30% без падения дохода мастеров.",
                facts: [
                    { title: "+30%", description: "ежегодный рост выручки GOLOVA" },
                    { title: "1–2 дня", description: "на внедрение системы" },
                    { title: "30+", description: "типовых авто-задач в библиотеке" },
                    { title: "40%→30%", description: "снижение доли ФОТ без потери дохода мастеров" },
                ],
            },
            skillsCards: [
                {
                    icon: "🎯",
                    title: "Авто-задачи каждый день",
                    description: "Администратор получает конкретное задание: что сделать и сколько. Не нужно напоминать — система ставит задачу сама.",
                },
                {
                    icon: "💰",
                    title: "Зарплата за результат",
                    description: "Схема Фикс + Бонус KPI + Премия от дневной выручки. Сотрудник понимает, за что получает деньги — и хочет делать больше.",
                },
                {
                    icon: "📊",
                    title: "Smart-дашборд",
                    description: "Руководитель видит картину за 5 минут: цветовая кодировка показывает, где выполнение, где провал, где рост.",
                },
                {
                    icon: "📚",
                    title: "Библиотека 30+ задач",
                    description: "Готовые авто-задачи под все аспекты бьюти-бизнеса: возврат, запись, средний чек, отзывы, кросс-продажи.",
                },
                {
                    icon: "⚡",
                    title: "Запуск за 1–2 дня",
                    description: "Готовые инструкции и обучение команды — система работает с первого дня без долгого внедрения.",
                },
                {
                    icon: "🏢",
                    title: "Масштаб на сеть",
                    description: "Подходит для управляющих компаний и сетей: несколько локаций без управляющего на каждой точке.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].salesTraining,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'matrix',
                    header: null,
                    plans: ["Базис", "Оптима"],
                    rows: [{ term: "1 месяц", cells: ["4 200 ₽", "4 600 ₽"] },
                        { term: "6 месяцев", cells: ["4 200 ₽/мес\n25 200 ₽", "4 600 ₽/мес\n27 600 ₽"] },
                        { term: "12 месяцев", cells: ["4 200 ₽/мес\n50 400 ₽", "4 600 ₽/мес\n55 200 ₽"] }],
                },
                facts: [
                    "Мотивационная схема Beauty-Autopilot® с грейдами",
                    "Smart-дашборд с цветовой кодировкой",
                    "Библиотека 30+ авто-задач",
                    "Готовые инструкции по внедрению",
                    "Обучение команды",
                ],
                partner: {
                    name: "Beauty-Autopilot®",
                    logoName: "BA",
                    subtitle: "Лев и Ирина Магай · сеть GOLOVA · через Data Heroes",
                },
                dhOfficial: false,
            },
        },
    },
    crmTO: {
        id: config.marketplaceModulesIds[config.buildType].crmTO,
        card: {},
        page: {
            headerCard: {
                icon: "🛠️",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Продолжаем работать с базой — системно, эффективно, по-человечески.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Настройка сделана уже давно — но сегменты устаревают, скрипты не обновляются, результаты падают. Нет времени или компетенции самостоятельно развивать систему. Система работает по инерции, а не на результат.",
                secondDescription: "ТО — это работы по обновлению для компаний, которые уже внедрили систему и хотят развития. Работаем в связке с управляющим, маркетологом или владельцем: создаём сезонный план, актуализируем сегменты и скрипты, настраиваем новые механики и оцениваем результат. Стартуем с аудита и встречи.",
                facts: [
                    { title: "1 день", description: "ответ на любой вопрос в течение рабочего дня" },
                    { title: "1 мес", description: "маркетинговый план на сезон" },
                    { title: "4 блока", description: "аудит · обновление · планирование · результат" },
                ],
            },
            skillsCards: [
                {
                    icon: "👤",
                    title: "Персональное ведение",
                    description: "Онлайн-встречи, быстрая поддержка, ответы на вопросы в течение 1 рабочего дня.",
                },
                {
                    icon: "🔍",
                    title: "Аудит и аналитика",
                    description: "Рекомендации по работе с базой, сегментам и точкам роста выручки.",
                },
                {
                    icon: "✏️",
                    title: "Скрипты и сегменты",
                    description: "Обновление текстов под сезон, тональность и поводы. Спецсегменты под акции, отмены, бонусы, триггеры.",
                },
                {
                    icon: "📅",
                    title: "Маркетинговое планирование",
                    description: "Сезонный план с офферами и инфоповодами. Настройка цепочек под текущие задачи. Идеи и тексты для рассылок.",
                },
                {
                    icon: "📋",
                    title: "План на каждый месяц",
                    description: "Чёткий план работ на месяц. Рекомендации по действиям команды на местах.",
                },
                {
                    icon: "📈",
                    title: "Новые механики",
                    description: "Внедряем новые сценарии и механики — и оцениваем их результат в цифрах.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].salesTraining,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Формат",
                    unit: "",
                    flagLast: false,
                    rows: [{ term: "Подписка", price: "40 000 ₽/мес" }],
                    note: "Только для клиентов с настроенным CRM-маркетингом. Старт: аудит + встреча → план на сезон. Возможна индивидуальная настройка под объём задач.",
                },
                facts: [
                    "Персональное ведение: онлайн-встречи и быстрая поддержка",
                    "Ответы на вопросы в течение 1 рабочего дня",
                    "Аудит базы, сегментов и точек роста",
                    "Обновление скриптов и сегментов под сезон",
                    "Создание спецсегментов под акции и триггеры",
                    "Сезонный маркетинговый план",
                    "Настройка новых цепочек и механик",
                    "Генерация идей и текстов для рассылок",
                ],
                dhOfficial: true,
            },
        },
    },
    zvonoBot: {
        id: config.marketplaceModulesIds[config.buildType].zvonoBot,
        card: {},
        page: {
            headerCard: {
                icon: "📞",
                badge: { color: '#293000', bcg: '#D4EC38', text: 'НОВИНКА' },
                tags: 'Аудиорассылки · Возврат клиентов',
                title: null,
                shortDescription: "Автоматически звонит клиентской базе, собирает тёплые обращения и передаёт их администратору в работу внутри Data Heroes.",
                extraInfoHtml: "Самостоятельный запуск · от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Аудиорассылки DH прозванивают выбранный сегмент клиентской базы. Система воспроизводит клиентам короткое сообщение и фиксирует результат каждого звонка.",
                secondDescription: "Если клиент нажимает 5, звонок переводится администратору или тёплое обращение попадает в список задач. Нажатие 3 добавляет клиента в список «Не звонить». Если клиент не ответил, система повторяет попытку по расписанию.",
                facts: [
                    { title: "Аудиорассылка", description: "Короткий звонок по выбранному сегменту клиентской базы." },
                    { title: "Нажал 5", description: "Тёплое обращение сразу передаётся администратору." },
                    { title: "Нажал 3", description: "Клиент больше не участвует в повторных прозвонах." },
                    { title: "До 3 попыток", description: "Повторяем звонок, если клиент не ответил с первого раза." },
                ],
            },
            skillsCards: [
                {
                    icon: "👥",
                    title: "Сегменты из Data Heroes",
                    description: "Выберите потерянных, горячих клиентов, готовый сегмент или загрузите собственный список.",
                },
                {
                    icon: "🎙️",
                    title: "Свой аудиоролик",
                    description: "Загрузите аудио на модерацию. Одобренные записи можно использовать в разных аудиорассылках.",
                },
                {
                    icon: "🔥",
                    title: "Передача тёплого лида",
                    description: "Нажавший 5 клиент соединяется с номером бизнеса или попадает в список задач.",
                },
                {
                    icon: "📵",
                    title: "Список «Не звонить»",
                    description: "Нажатие 3 исключает клиента из звонков. Перед вызовом проверяются отметки «Не звонить» и «Не напоминаем».",
                },
                {
                    icon: "🔁",
                    title: "Повторные попытки",
                    description: "Настройте утреннее, дневное или вечернее окно и интервалы между звонками.",
                },
                {
                    icon: "📊",
                    title: "Аналитика результата",
                    description: "Смотрите дозвоны, отклики, лиды, записи и итог работы каждой аудиорассылки.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].sms,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].dhChat,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].customCascades,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: null,
                    unit: "/сек.",
                    flagLast: false,
                    rows: [{ term: "Телефония", price: "0,10 ₽" }],
                    note: "Предоплата: пополните баланс во вкладке «Телефония» внутри модуля.\n\nОплачивается отдельно. Списывается только фактическое время разговора.",
                },
                facts: [
                    "Выбор сегмента из клиентской базы",
                    "Аудиотека с модерацией записей",
                    "Настройка сценария и расписания",
                    "Передача откликов администратору",
                    "Отказ от звонков и повторные попытки",
                    "Результаты каждой аудиорассылки",
                ],
                dhOfficial: true,
            },
        },
    },
    salesTraining: {
        id: config.marketplaceModulesIds[config.buildType].salesTraining,
        card: {},
        page: {
            headerCard: {
                icon: "🔥",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "После 7 часов живой практики администратор ведёт клиента к записи без давления, работает с возражениями и делает допродажи органично.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Администраторы работают как справочное бюро: отвечают на вопросы, но не продают. «Дорого» — и разговор заканчивается. Боятся предложить доп. услугу. Бегут с каждым вопросом к управляющей. Клиент уходит без записи и без допродажи.",
                secondDescription: "Авторский тренинг Сергея Теймурова — 2 дня, 3,5 часа каждый, онлайн, живой формат, группа до 10 человек. Покупает владелец или управляющий, проходит администратор. Только кейсы из бьюти — никакого абстрактного бизнеса. Авторские методологии: РВД (8 состояний), «2 минуты», Метасообщения, Айкидо в коммуникации, 3 типа ответов, 5 вопросов, 3 вопроса-фильтра. Методичка 7 блоков остаётся у участника навсегда.",
                facts: [
                    { title: "7 часов", description: "живой практики за 2 дня" },
                    { title: "до 10", description: "человек в группе — закрытый формат" },
                    { title: "7 блоков", description: "в методичке, которая остаётся навсегда" },
                    { title: "14 лет", description: "опыт автора в маркетинге бьюти" },
                ],
            },
            skillsCards: [
                {
                    icon: "🥋",
                    title: "Айкидо в коммуникации",
                    description: "Ведёт клиента к записи без давления и сопротивления. Принципы работают в переписке и живом разговоре.",
                },
                {
                    icon: "💬",
                    title: "3 типа ответов",
                    description: "Игра · Отговорка · Боль — как распознать тип возражения и ответить точно, а не шаблонно.",
                },
                {
                    icon: "🔎",
                    title: "5 вопросов",
                    description: "Точная диагностика потребности клиента. Администратор выявляет, что нужно, — и предлагает именно это.",
                },
                {
                    icon: "🧠",
                    title: "РВД — 8 состояний",
                    description: "Модель управления собой. Администратор понимает своё состояние и умеет быстро войти в рабочий режим.",
                },
                {
                    icon: "💡",
                    title: "Самостоятельные решения",
                    description: "3 вопроса-фильтра вместо похода к управляющей. Меньше нагрузки на руководителя.",
                },
                {
                    icon: "📓",
                    title: "Методичка навсегда",
                    description: "7 блоков с техниками, шаблонами и инструментами. Остаётся у участника после тренинга.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Состав участников",
                    unit: "",
                    flagLast: false,
                    rows: [{ term: "Администратор", price: "23 000 ₽" },
                        { term: "Администратор + управляющий", price: "35 000 ₽" }],
                    note: "Онлайн, живой формат (не запись), группа до 10 человек. Запись через менеджера. Доступна рассрочка по запросу.",
                },
                facts: [
                    "2 дня живого онлайн-тренинга (3,5 ч каждый)",
                    "Авторские методологии: РВД, Айкидо, Метасообщения, 3 типа ответов, 5 вопросов",
                    "Методичка 7 блоков — остаётся навсегда",
                    "Диагностический лист по администратору",
                    "Рекомендации управляющему",
                    "Доступна рассрочка по запросу",
                ],
                dhOfficial: true,
            },
        },
    },
    kpiSettings: {
        id: config.marketplaceModulesIds[config.buildType].kpiSettings,
        card: {},
        page: {
            headerCard: {
                icon: "🎯",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Один админ конвертирует 30% тёплых лидов — другой 8%. Зарплата одинакова. Настройка KPI это исправляет.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Когда CRM-маркетинг начал работать, система передаёт администраторам тёплых клиентов: звонки, ручные касания, работу с потерявшимися. Как понять, кто реально работает с базой, а кто просто отмечает галочки? Без KPI вы платите за процесс, а не за результат.",
                secondDescription: "Разовая сессия с экспертом — по итогам у вас готовая система мотивации, привязанная к реальным показателям выручки. Формулы расчёта бонусов под ваши активации в Data Heroes. Личный дашборд для каждого сотрудника. Правила начисления понятны заранее — не нужно объяснять мотивацию каждый месяц. Бонус растёт, когда растёт результат: плати за возврат клиента, а не за звонок. Лучше всего настраивать сразу после финального запуска основной настройки.",
                facts: [
                    { title: "1 созвон", description: "с экспертом — и система готова" },
                    { title: "5 000 ₽", description: "фиксированная стоимость без скрытых платежей" },
                    { title: "2 части", description: "бонус за коммуникацию + бонус за посещение" },
                ],
            },
            skillsCards: [
                {
                    icon: "📐",
                    title: "Формулы под ваши активации",
                    description: "Не абстрактные KPI, а конкретные цифры под вашу воронку и сценарии в Data Heroes.",
                },
                {
                    icon: "👤",
                    title: "Личный дашборд сотрудника",
                    description: "Администратор видит: сколько коммуникаций, какая конверсия, сколько заработал.",
                },
                {
                    icon: "💡",
                    title: "Прозрачность без объяснений",
                    description: "Правила начисления понятны заранее — не нужно объяснять мотивацию каждый месяц.",
                },
                {
                    icon: "💰",
                    title: "KPI связан с выручкой",
                    description: "Бонус за факт коммуникации + бонус за факт посещения после коммуникации. Платишь за результат.",
                },
                {
                    icon: "⚡",
                    title: "Готово за один созвон",
                    description: "Настраиваем KPI прямо в Data Heroes под ваши активации и команду. Готовые формулы остаются у вас.",
                },
                {
                    icon: "🕐",
                    title: "Лучший момент — после настройки",
                    description: "Пока все активации свежие и эксперт понимает вашу воронку.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Формат",
                    unit: "",
                    flagLast: false,
                    rows: [{ term: "Разовая сессия", price: "5 000 ₽" }],
                    note: "Один платёж. Стоимость фиксированная, без скрытых платежей.",
                },
                facts: [
                    "Один созвон с экспертом",
                    "Формулы расчёта бонусов под ваши активации",
                    "Личный дашборд для каждого сотрудника",
                    "Настройка прямо в Data Heroes",
                    "Готовые формулы остаются у вас",
                ],
                dhOfficial: true,
            },
        },
    },
    maxBusiness: {
        id: config.marketplaceModulesIds[config.buildType].maxBusiness,
        card: {},
        page: {
            headerCard: {
                icon: "💬",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Официальный чат-бот MAX снижает риск блокировок: бот зарегистрирован как бизнес-отправитель и работает по правилам платформы.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "При отправке рассылок через мессенджеры сообщения могут попадать под блокировки. Один номер — один канал — и он периодически замолкает. Часть базы не получает сообщения. Вы теряете касания, не зная об этом.",
                secondDescription: "Подключите официальный чат-бот MAX как канал отправки. Бот зарегистрирован на платформе как бизнес-отправитель — меньше фильтрации, выше доставляемость. Встраивается в существующий каскад без изменения логики приоритетов. Альтернатива номерному каналу или дополнение к нему. Важно: клиент должен самостоятельно запустить вашего чат-бота MAX для получения сообщений через этот канал.",
                facts: [
                    { title: "1 шаг", description: "подключение через токен бота" },
                    { title: "0 изменений", description: "в логике каскада при добавлении канала" },
                    { title: "Меньше блоков", description: "за счёт статуса бизнес-отправителя" },
                ],
            },
            skillsCards: [
                {
                    icon: "🔒",
                    title: "Официальный отправитель",
                    description: "Бот зарегистрирован на платформе MAX как бизнес-отправитель. Снижает риск фильтрации и блокировок.",
                },
                {
                    icon: "🔁",
                    title: "Участие в каскаде",
                    description: "Канал встраивается в существующую цепочку отправки без изменения логики приоритетов.",
                },
                {
                    icon: "📲",
                    title: "Альтернатива номеру",
                    description: "Можно использовать вместо номерного канала MAX или добавить как дополнительный.",
                },
                {
                    icon: "⚙️",
                    title: "Простое подключение",
                    description: "Подключается через токен бота за несколько минут. Статус и название бота отображаются в интерфейсе.",
                },
                {
                    icon: "📊",
                    title: "Статус ошибок",
                    description: "Если клиент не запустил бота — вы видите понятный статус ошибки, а не молчание.",
                },
                {
                    icon: "🔗",
                    title: "Тестовое сообщение",
                    description: "После подключения можно сразу отправить тестовое сообщение на номер и проверить доставку.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Цена за месяц по сроку",
                    unit: "/мес",
                    flagLast: true,
                    rows: [{ term: "1 месяц", price: "1 500 ₽" },
                        { term: "3 месяца", price: "1 350 ₽" },
                        { term: "6 месяцев", price: "1 230 ₽" },
                        { term: "12 месяцев", price: "1125 ₽" },
                        { term: "24 месяца", price: "900 ₽" }],
                    note: null,
                },
                facts: [
                    "Подключение чат-бота MAX как канала отправки",
                    "Интеграция в существующий каскад",
                    "Отображение статуса и названия бота",
                    "Инструкция по получению токена",
                    "Описание ошибок отправки",
                ],
                dhOfficial: true,
            },
        },
    },
    channels: {
        id: config.marketplaceModulesIds[config.buildType].channels,
        card: {},
        page: {
            headerCard: {
                icon: "📡",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Один канал — неполный охват. Telegram и MAX Business дают второй и третий шанс достучаться до каждого клиента.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Вы отправляете напоминания, акции и сообщения о текущей записи через один подключённый канал. Если клиент там неактивен — сообщение не дойдёт. Пустое окно, забытая запись, потерянная выручка. Вы об этом не узнаете, пока не посчитаете итог недели.",
                secondDescription: "Новые каналы расширяют зону охвата: подключите Telegram и/или MAX Business, настройте каскад приоритетов — и система сама найдёт путь к каждому клиенту. MAX Business — официальный бот-отправитель, зарегистрированный на платформе: меньше блокировок, выше доставляемость. Telegram — там, где клиент проводит время. Вместе они закрывают бреши, которые один канал оставляет открытыми. Также подходит для подключения резервного номера или дополнительной точки контакта.",
                facts: [
                    { title: "30–60%", description: "доставляемость через один канал" },
                    { title: "3×", description: "больше шансов дойти до клиента при каскаде" },
                    { title: "1–3 дня", description: "до запуска нового канала" },
                ],
            },
            skillsCards: [
                {
                    icon: "📡",
                    title: "Каскад каналов",
                    description: "Сообщение ищет адресата: не доставлено в первый канал — уходит во второй. Ни один клиент не выпадает из коммуникации.",
                },
                {
                    icon: "🔒",
                    title: "Меньше блокировок",
                    description: "Чем больше каналов, тем меньше риск получить блок.",
                },
                {
                    icon: "🏢",
                    title: "Раздели отделы",
                    description: "Если у вас выделенный колл-центр или работа разделена между разными людьми — настройте разные каскады и добавьте несколько номеров.",
                },
                {
                    icon: "🔁",
                    title: "Гибкая приоритизация",
                    description: "Вы сами определяете порядок каналов в каскаде. Логика отправки не меняется — просто становится шире.",
                },
                {
                    icon: "📊",
                    title: "Статус доставки",
                    description: "Видите, через какой канал дошло сообщение и где случился сбой. Не гадаете — знаете.",
                },
                {
                    icon: "📞",
                    title: "Резервный номер",
                    description: "Подключите дополнительный номер или канал как запасной — на случай блокировок или технических сбоев.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Цена за месяц по сроку",
                    unit: "/мес",
                    flagLast: true,
                    rows: [{ term: "1 месяц", price: "1 000 ₽" },
                        { term: "3 месяца", price: "900 ₽" },
                        { term: "6 месяцев", price: "820 ₽" },
                        { term: "12 месяцев", price: "750 ₽" },
                        { term: "24 месяца", price: "600 ₽" }],
                    note: "Чем длиннее срок — тем ниже цена за месяц.",
                },
                facts: [
                    "Подключение Telegram к Доктор-боту / Умному-боту",
                    "Подключение MAX Business (официальный чат-бот)",
                    "Настройка каскада приоритетов отправки",
                    "Инструкция для администратора",
                    "Резервный номер по запросу",
                ],
                dhOfficial: true,
            },
        },
    },
    dhChat: {
        id: config.marketplaceModulesIds[config.buildType].dhChat,
        card: {},
        page: {
            headerCard: {
                icon: "💬",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: null,
                title: null,
                shortDescription: "Все входящие сообщения из WhatsApp, Telegram, MAX и MAX Business — в одном окне Data Heroes. Администратор отвечает быстрее, не переключается между устройствами и работает с WhatsApp и Telegram без включённого VPN.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Когда клиенты пишут в разные мессенджеры, администратор вынужден постоянно переключаться между вкладками, телефонами и приложениями. Часть диалогов теряется, ответы задерживаются, а руководитель не видит единой картины коммуникаций.",
                secondDescription: "Единый чат собирает клиентские сообщения в одном интерфейсе Data Heroes. Каналы WhatsApp и Telegram в едином чате работают без VPN: вы получаете и отправляете сообщения без включённого VPN на устройстве. Единый чат входит в тариф «Умный Бот» и доступен при покупке канала MAX Business. Для тарифа «Доктор Бот» его можно подключить отдельно за 1 000 ₽ в месяц.",
                facts: [
                    { title: "Без VPN", description: "WhatsApp и Telegram работают прямо в интерфейсе DH" },
                    { title: "1 окно", description: "все клиентские диалоги и каналы в одном месте" },
                    { title: "1 000 ₽", description: "доплата в месяц для тарифа «Доктор Бот»" },
                    { title: "Включено", description: "в «Умный Бот» и доступно с MAX Business" },
                ],
            },
            skillsCards: [
                {
                    icon: "💬",
                    title: "Все диалоги в одном окне",
                    description: "WhatsApp, Telegram, MAX и MAX Business собираются в единой ленте. Администратор не прыгает между приложениями.",
                },
                {
                    icon: "🛡️",
                    title: "WhatsApp и Telegram без VPN",
                    description: "Сообщения принимаются и отправляются без включённого VPN на рабочем устройстве администратора.",
                },
                {
                    icon: "⚡",
                    title: "Быстрее ответ клиенту",
                    description: "Новые сообщения видны сразу. Менеджер открывает диалог и отвечает из интерфейса Data Heroes.",
                },
                {
                    icon: "👤",
                    title: "Связь с клиентской базой",
                    description: "Диалог можно вести в контексте клиента, его записи, сегмента и истории коммуникаций.",
                },
                {
                    icon: "🔔",
                    title: "Меньше потерянных сообщений",
                    description: "Пуши и счётчики непрочитанных помогают не пропустить вопрос, перенос записи или запрос на услугу.",
                },
                {
                    icon: "🧩",
                    title: "Гибкая коммерческая логика",
                    description: "Отдельная покупка для «Доктор Бота» или автоматический доступ через «Умный Бот» и MAX Business.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].maxBusiness,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].channels,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].kpiSettings,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Доступность",
                    unit: null,
                    flagLast: false,
                    bestRowIndex: 0,
                    showBestFlag: false,
                    rows: [{ term: "Доктор Бот", price: "1 000 ₽/мес" },
                        { term: "Умный Бот", price: "Включено" },
                        { term: "MAX Business", price: "Доступно" }],
                    note: null,
                },
                facts: [
                    "Единая лента диалогов по каналам",
                    "WhatsApp и Telegram работают без VPN",
                    "Получение и отправка сообщений из Data Heroes",
                    "Фильтрация по каналам и непрочитанным",
                    "Доступ через «Умный Бот» или MAX Business",
                ],
                dhOfficial: true,
            },
        },
    },
    diagnostics: {
        id: config.marketplaceModulesIds[config.buildType].diagnostics,
        card: {},
        page: {
            headerCard: {
                icon: "🔎",
                badge: { color: '#AAB2C4', bcg: null, text: 'Консультация эксперта · Бесплатно' },
                tags: '',
                title: null,
                shortDescription: "Разберём базу, посчитаем потенциал и покажем, сколько выручки теряется прямо сейчас.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Непонятно, с чего начать. CRM есть, но непонятно, что в ней происходит. Клиенты уходят — причина неочевидна. Рассылки делаем, но результата не видно.",
                secondDescription: "Бесплатная диагностика — персональная встреча с экспертом. Разбираем текущее состояние CRM-маркетинга, смотрим на базу, считаем потенциал возврата. По итогам — конкретные рекомендации и понимание, какой продукт решит вашу задачу. Без обязательств и давления.",
                facts: [
                    { title: "0 ₽", description: "стоимость диагностики" },
                    { title: "1 встреча", description: "с экспертом по CRM-маркетингу в бьюти" },
                    { title: "Рекомендации", description: "конкретные по итогам — не общие слова" },
                ],
            },
            skillsCards: [
                {
                    icon: "🔍",
                    title: "Анализ текущей ситуации",
                    description: "Смотрим на CRM, базу и коммуникации. Находим, где теряется выручка.",
                },
                {
                    icon: "💰",
                    title: "Расчёт потенциала",
                    description: "Считаем, сколько клиентов можно вернуть и какую выручку это даст.",
                },
                {
                    icon: "📋",
                    title: "Конкретные рекомендации",
                    description: "По итогам встречи — план действий, а не общие слова.",
                },
                {
                    icon: "🎯",
                    title: "Подбор продукта",
                    description: "Понимаем, какое решение подойдёт именно вашему бизнесу.",
                },
                {
                    icon: "🤝",
                    title: "Без обязательств",
                    description: "Диагностика бесплатна. Никакого давления и навязывания.",
                },
                {
                    icon: "⚡",
                    title: "Быстрый старт",
                    description: "Встреча занимает 30–60 минут. По итогам — ясность и следующий шаг.",
                },
            ],
            otherIntegrations: [
                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: null,
                    unit: null,
                    flagLast: false,
                    bestRowIndex: 0,
                    showBestFlag: false,
                    rows: null,
                    note: null,
                },
                facts: [
                    "Персональная встреча с экспертом по CRM-маркетингу",
                    "Анализ текущего состояния базы и коммуникаций",
                    "Расчёт потенциала возврата клиентов",
                    "Конкретные рекомендации по итогам",
                    "Подбор подходящего продукта под задачи",
                ],
                dhOfficial: true,
            },
        },
    },
    customCascades: {
        id: config.marketplaceModulesIds[config.buildType].customCascades,
        card: {},
        page: {
            headerCard: {
                icon: "📈",
                badge: { color: '#252834', bcg: '#D4EC38', text: 'НОВИНКА' },
                tags: 'Автоматизация · Рассылки',
                title: null,
                shortDescription: "Настраивайте разные порядки отправки для команд, направлений и сегментов базы. Каждый каскад использует общие каналы проекта, но собственные приоритеты.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "В стандартной настройке проект использует один порядок каналов. Этого достаточно, пока все сообщения отправляются одинаково. В клиниках, сетях и проектах с несколькими командами разным группам нужен собственный порядок Telegram, WhatsApp, MAX и других каналов.",
                secondDescription: "Дополнительные каскады позволяют создать до 10 именованных конфигураций. Например, для администраторов сначала использовать MAX, для менеджеров — WhatsApp, а для отдельного направления исключить один из каналов.",
                facts: [
                    { title: "10", description: "дополнительных каскадов в проекте" },
                    { title: "1–3", description: "клика для изменения приоритета" },
                    { title: "Общие", description: "каналы для всех каскадов" },
                    { title: "Авто", description: "переход к следующему доступному каналу" },
                ],
            },
            skillsCards: [
                {
                    icon: "🔟",
                    title: "До 10 дополнительных каскадов",
                    description: "Создавайте именованные конфигурации от Каскада 2 до Каскада 11.",
                },
                {
                    icon: "🔀",
                    title: "Независимые приоритеты",
                    description: "Один канал может быть первым в одном каскаде и не участвовать в другом.",
                },
                {
                    icon: "🏷️",
                    title: "Понятные названия",
                    description: "Назовите каскады по команде, филиалу, направлению или сегменту базы.",
                },
                {
                    icon: "📡",
                    title: "Общий набор каналов",
                    description: "Каналы принадлежат проекту. Все каскады используют актуальный список подключений.",
                },
                {
                    icon: "📨",
                    title: "Выбор в рассылках",
                    description: "Настроенные каскады появляются в рассылках и триггерных сценариях.",
                },
                {
                    icon: "🛡️",
                    title: "Безопасное удаление",
                    description: "После удаления отправки автоматически переходят на канал отправки по умолчанию.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].dhChat,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].channels,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: null,
                facts: [
                    "До 10 дополнительных каскадов",
                    "Именованные конфигурации для команд и сегментов",
                    "Выбор каскада в рассылках и сценариях",
                    "Автоматический переход к следующему каналу",
                    "Единые права доступа с основным каскадом",
                ],
                dhOfficial: true,
            },
        },
    },
    sms: {
        id: config.marketplaceModulesIds[config.buildType].sms,
        card: {},
        page: {
            headerCard: {
                icon: "📱",
                badge: { color: '#D4EC38', bcg: '#1A1A2E', text: null },
                tags: 'Каналы · коммуникации · Android',
                title: null,
                shortDescription: "Отправляйте SMS с рабочего номера салона и получайте ответы клиентов. Android-телефон с SIM-картой становится управляемым каналом Data Heroes.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "SMS DH превращает Android-телефон с рабочей SIM-картой в канал отправки Data Heroes. Пользователь добавляет канал в разделе «Каналы отправки», сканирует QR-код в приложении, выбирает SIM 1 или SIM 2 и указывает номер выбранной SIM-карты.",
                secondDescription: "Сообщения уходят с номера салона, поэтому клиент видит знакомого отправителя и может ответить на SMS. Входящие ответы сохраняются в рабочем контуре, а состояние телефона, очередь отправки и ошибки доступны для диагностики.",
                facts: [
                    { title: "С номера салона", description: "Клиент видит рабочий номер, а не обезличенного отправителя." },
                    { title: "Двусторонняя связь", description: "Исходящие SMS и ответы клиентов доступны в Data Heroes." },
                    { title: "QR-код", description: "Подключите телефон без ручного ввода токенов." },
                    { title: "SIM 1 / SIM 2", description: "Отдельный канал для каждой физической SIM-карты." },
                ],
            },
            skillsCards: [
                {
                    icon: "💬",
                    title: "SMS с рабочего номера",
                    description: "Сообщения отправляются с SIM-карты салона. Клиент видит номер, который уже знает.",
                },
                {
                    icon: "📥",
                    title: "Ответ клиента не теряется",
                    description: "Входящие SMS фиксируются в приложении и доступны для последующей обработки командой.",
                },
                {
                    icon: "📲",
                    title: "Подключение по QR",
                    description: "Добавьте SMS-канал в Data Heroes и отсканируйте код Android-телефоном.",
                },
                {
                    icon: "💳",
                    title: "Выбор SIM-карты",
                    description: "Для каждого канала выберите физический слот SIM 1 или SIM 2 и укажите его номер.",
                },
                {
                    icon: "✍️",
                    title: "Отдельные SMS-тексты",
                    description: "В триггерах используется отдельный короткий текст со счётчиком длины и SMS-сегментов.",
                },
                {
                    icon: "📡",
                    title: "Контроль состояния",
                    description: "Видны онлайн-статус телефона, SIM-карта, разрешения, очередь и ошибки отправки.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].maxBusiness,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].dhChat,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].customCascades,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Цена за месяц по сроку",
                    unit: "/мес",
                    flagLast: true,
                    rows: [{ term: "1 месяц", price: "1 000 ₽" },
                        { term: "3 месяца", price: "900 ₽" },
                        { term: "6 месяцев", price: "820 ₽" },
                        { term: "12 месяцев", price: "750 ₽" },
                        { term: "24 месяца", price: "600 ₽" }],
                    note: "Чем длиннее срок — тем ниже цена за месяц.",
                },
                facts: [
                    "Отправка с рабочего номера салона",
                    "Получение входящих ответов",
                    "Подключение Android-телефона по QR",
                    "Выбор SIM 1 или SIM 2",
                    "Отдельные SMS-тексты в триггерах",
                    "Диагностика телефона и очереди",
                ],
                dhOfficial: true,
            },
        },
    },
    businessPulseReport: {
        id: config.marketplaceModulesIds[config.buildType].businessPulseReport,
        card: {},
        page: {
            headerCard: {
                icon: "📈",
                badge: { color: '#D4EC38', bcg: '#1A1A2E', text: null },
                tags: null,
                title: null,
                shortDescription: "Собирает финансовые, клиентские и операционные показатели в одной приборной панели. Сравнивает периоды и показывает динамику бизнеса без ручного сведения нескольких отчётов.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Отчёт 2.0 — единая приборная панель Data Heroes для собственника и управляющего. В одном месте собраны выручка, клиентская база и записи за выбранный период.",
                secondDescription: "Один период применяется ко всему отчёту. Показатели можно сравнить с непосредственно предыдущим интервалом, с теми же календарными датами прошлого года или смотреть без сравнения. Первая версия показывает фактические данные без рекомендаций и автоматических выводов.",
                facts: [
                    { title: "17 показателей", description: "финансы, клиенты и записи в одном отчёте" },
                    { title: "3 раздела", description: "финансы, клиентская база и операционные показатели" },
                    { title: "3 сравнения", description: "предыдущий период, прошлый год или без сравнения" },
                    { title: "1 график", description: "выручка, все записи или средний чек по времени" },
                ],
            },
            skillsCards: [
                {
                    icon: "💰",
                    title: "Финансы",
                    description: "Выручка, услуги, товары, средний чек, дополнительные услуги и средняя выручка за смену.",
                },
                {
                    icon: "👥",
                    title: "Клиентская база",
                    description: "Новые и постоянные клиенты, возвращаемость и отток в одном разделе.",
                },
                {
                    icon: "📅",
                    title: "Записи и визиты",
                    description: "Все записи, завершённые визиты, отмены и состав записей по типу клиента.",
                },
                {
                    icon: "🔄",
                    title: "Сравнение периодов",
                    description: "Сравнивайте с предыдущим интервалом или теми же календарными датами прошлого года.",
                },
                {
                    icon: "📈",
                    title: "Динамика во времени",
                    description: "Переключайте график между выручкой, всеми записями и средним чеком.",
                },
                {
                    icon: "📐",
                    title: "Единая методика",
                    description: "Одинаковый период и одинаковая логика сравнения применяются ко всем показателям отчёта.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].opz2,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].sms,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].customCascades,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].maxBusiness,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: null,
                facts: [
                    "17 фактических показателей бизнеса",
                    "Финансы, клиенты и записи в одном экране",
                    "Сравнение с предыдущим периодом или прошлым годом",
                    "График выручки, записей и среднего чека",
                    "Единый период для всех показателей",
                ],
                dhOfficial: true,
            },
        },
    },
    opz2: {
        id: config.marketplaceModulesIds[config.buildType].opz2,
        card: {},
        page: {
            headerCard: {
                icon: { svg: OPZ2_ICON_SVG },
                badge: { color: '#D4EC38', bcg: '#1A1A2E', text: null },
                tags: null,
                title: null,
                shortDescription: "Показывает, как администраторы перезаписывают клиентов: на то же направление, к другому мастеру и на дополнительные услуги. Отдельно показывает факт следующего визита, отмены и KPI.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "ОПЗ 2.0 помогает руководителю видеть не только факт повторной записи, но и результат работы администратора: куда записали клиента, состоялся ли следующий визит, была ли отмена и появилась ли кросс-продажа.",
                secondDescription: "Повторная запись на то же направление относится к ОПЗ 2.0, даже если клиента записали к другому мастеру того же типа услуг. Переход на другой тип услуги учитывается как кросс-продажа. В детализации видно конкретного клиента и сотрудника, который оформил запись.",
                facts: [
                    { title: "2 разреза", description: "повторная запись и кросс-продажи считаются отдельно" },
                    { title: "До клиента", description: "видно, кого записали и чем закончился следующий визит" },
                    { title: "Статус визита", description: "будущая запись, завершённый визит или отмена" },
                    { title: "KPI админов", description: "результат перезаписи в разрезе сотрудников" },
                ],
            },
            skillsCards: [
                {
                    icon: "🔄",
                    title: "ОПЗ на то же направление",
                    description: "Отдельно показывает повторную запись на тот же тип услуг.",
                },
                {
                    icon: "👤",
                    title: "Запись к другому мастеру",
                    description: "Показывает ОПЗ к другому мастеру того же направления, не смешивая её с кросс-продажей.",
                },
                {
                    icon: "💰",
                    title: "Кросс-продажи",
                    description: "Отдельно считает переход на другой тип услуги и отмечает первые визиты.",
                },
                {
                    icon: "📅",
                    title: "Факт следующего визита",
                    description: "Видно, завершился ли визит, запланирован на будущее или был отменён.",
                },
                {
                    icon: "🔎",
                    title: "Детализация по клиентам",
                    description: "Можно перейти от общей цифры к клиенту, мастеру, дате записи и аккаунту администратора.",
                },
                {
                    icon: "📊",
                    title: "KPI администраторов",
                    description: "Сравнивайте ОПЗ и кросс-продажи по сотрудникам и настраивайте правила премирования.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].sms,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].customCascades,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].maxBusiness,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: null,
                facts: [
                    "ОПЗ на то же направление и к другому мастеру",
                    "Кросс-продажи и признак «Впервые»",
                    "Статусы следующего визита и отмены",
                    "Детализация по клиентам и мастерам",
                    "KPI администраторов и настройки премирования",
                ],
                dhOfficial: true,
            },
        },
    },
    hotWindows: {
        id: config.marketplaceModulesIds[config.buildType].hotWindows,
        card: {},
        page: {
            headerCard: {
                icon: "🔥",
                badge: { color: '#D4EC38', bcg: '#1A1A2E', text: null },
                tags: 'АВТОМАТИЗАЦИЯ',
                title: null,
                shortDescription: "Находят свободные окна сегодня и завтра, подбирают подходящих клиентов и помогают предложить перенос или новую запись без дополнительного рекламного бюджета.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Горячие окна превращают свободный интервал в готовую задачу для администратора. Система показывает окна на сегодня и завтра и сразу предлагает клиентов, которым можно отправить персональное предложение.",
                secondDescription: "Для окна сегодня можно перенести клиента с завтра на день раньше или пригласить клиента без будущей записи. Для окна завтра доступны клиенты без записи и переносы с послезавтра на завтра.",
                facts: [
                    { title: "2 дня", description: "окна на сегодня и завтра" },
                    { title: "2 задачи", description: "перенос или новая запись" },
                    { title: "До клиента", description: "подбор по истории и привычкам" },
                    { title: "₽ потенциал", description: "оценка свободных окон" },
                ],
            },
            skillsCards: [
                {
                    icon: "🗓️",
                    title: "Окна сегодня и завтра",
                    description: "Свободные интервалы сгруппированы по дню, специалисту и времени.",
                },
                {
                    icon: "⏪",
                    title: "Перенос на день раньше",
                    description: "Для окна сегодня подбираются клиенты с записью на завтра, для окна завтра — с послезавтра.",
                },
                {
                    icon: "👤",
                    title: "Клиенты без будущей записи",
                    description: "Система находит клиентов, которым пора вернуться, и предлагает свободный слот.",
                },
                {
                    icon: "🎯",
                    title: "Подбор по привычкам",
                    description: "Учитываются история визитов, цикл услуги, знакомство с мастером и привычное время.",
                },
                {
                    icon: "💬",
                    title: "Персональное предложение",
                    description: "Администратор видит лучший вариант и может сразу предложить перенос или новое окно.",
                },
                {
                    icon: "💰",
                    title: "Потенциал свободных окон",
                    description: "Для дня показывается потенциальная выручка свободных интервалов.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].businessPulseReport,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].opz2,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].sms,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].maxBusiness,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: null,
                facts: [
                    "Свободные окна на сегодня и завтра",
                    "Перенос записи на день раньше",
                    "Клиенты без будущей записи",
                    "Подбор по истории, циклу и привычному времени",
                    "Персональное предложение и потенциал окна",
                ],
                dhOfficial: true,
            },
        },
    },
    webinars: {
        id: config.marketplaceModulesIds[config.buildType].webinars,
        card: {},
        page: {
            headerCard: {
                icon: "🔥",
                badge: { color: '#1E9E5A', bcg: 'rgba(30,158,90,.13)', text: 'РЕКОМЕНДУЕМ' },
                tags: 'Обучение · Бесплатно',
                title: null,
                shortDescription: "Как выстроить CRM-маркетинг, который возвращает клиентов сам — без хаотичных рассылок и ручной работы администратора.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Салоны делают рассылки, но не видят системного результата. Непонятно, кому писать, с каким оффером и когда. CRM используется как хранилище телефонов, а не как инструмент возврата.",
                secondDescription: "Бесплатный вебинар — разбор методологии CRM-маркетинга для бьюти-бизнеса. Как сегментировать базу, какие сценарии работают, как настроить цикличность визитов и перестать терять клиентов. Живой формат, практические примеры из реальных салонов.",
                facts: [
                    { title: "0 ₽", description: "стоимость участия" },
                    { title: "Живой формат", description: "практические примеры из реальных салонов" },
                    { title: "14 лет", description: "опыт автора в маркетинге бьюти-индустрии" },
                ],
            },
            skillsCards: [
                {
                    icon: "🎓",
                    title: "Методология CRM-маркетинга",
                    description: "Как выстроить систему возврата клиентов, а не разовые рассылки.",
                },
                {
                    icon: "🗂️",
                    title: "Сегментация базы",
                    description: "Как делить клиентов по поведению и жизненному циклу — и что с каждым сегментом делать.",
                },
                {
                    icon: "🔄",
                    title: "Сценарии возврата",
                    description: "Какие цепочки работают, какие нет — разбор на реальных примерах.",
                },
                {
                    icon: "📅",
                    title: "Цикличность визитов",
                    description: "Как удерживать регулярность — без давления на клиента.",
                },
                {
                    icon: "💬",
                    title: "Живые примеры",
                    description: "Кейсы из реальных салонов красоты, студий и клиник косметологии.",
                },
                {
                    icon: "🤝",
                    title: "Без обязательств",
                    description: "Бесплатный вебинар. Никакого навязывания.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].zvonoBot,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: null,
                facts: [
                    "Живой вебинар с Сергеем Теймуровым",
                    "Методология CRM-маркетинга для бьюти",
                    "Разбор сегментации и сценариев возврата",
                    "Реальные кейсы из салонов",
                    "Запись для зарегистрировавшихся",
                ],
                dhOfficial: true,
            },
        },
    },
    opz2KPISettings: {
        id: config.marketplaceModulesIds[config.buildType].opz2KPISettings,
        card: {},
        page: {
            headerCard: {
                icon: { mdi: 'mdi-cog' },
                badge: { color: '#1E9E5A', bcg: 'rgba(30,158,90,.13)', text: null },
                tags: 'Услуги · Мотивация',
                title: null,
                shortDescription: "Перенесём вашу схему премирования в ОПЗ 2.0: действия, ставки и условия начисления.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Настроим премии за повторную запись и кросс-продажу, чтобы команда видела связь между своими действиями и результатом.",
                secondDescription: "Выберем оплачиваемые действия, зададим ставки, сопоставим сотрудников и проверим расчёт на примерах.",
                facts: null,
            },
            skillsCards: [
                {
                    icon: "✅",
                    title: "Действия и условия",
                    description: "Выберем правила за повторную запись, кросс-продажу и другие действия, доступные в ОПЗ 2.0.",
                },
                {
                    icon: "💰",
                    title: "Суммы и проценты",
                    description: "Зададим фиксированные ставки, процент от стоимости визита или их сочетание.",
                },
                {
                    icon: "⏱️",
                    title: "Момент начисления",
                    description: "Укажем, когда считать премию: при записи, после визита или после закрытия.",
                },
                {
                    icon: "👥",
                    title: "Администраторов",
                    description: "Сопоставим аккаунты и сотрудников, чтобы начисления относились к нужному человеку.",
                },
                {
                    icon: "🏷️",
                    title: "Направления мастеров",
                    description: "Проверим категории услуг, чтобы ОПЗ и кросс-продажи разделялись корректно.",
                },
                {
                    icon: "🔍",
                    title: "Проверку расчёта",
                    description: "Разберём начисления, отмены и переносы на примерах из отчёта.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].opz2,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Настройка KPI с экспертом Data Heroes",
                    unit: "",
                    flagLast: false,
                    rows: null,
                    note: "Подписка на ОПЗ 2.0 оплачивается отдельно.",
                },
                facts: [
                    "Правила премирования за ОПЗ и кросс-продажи",
                    "Ставки и условия начисления",
                    "Сопоставление администраторов и направлений",
                    "Проверка расчёта на примерах",
                ],
                dhOfficial: true,
            },
        },
    },
    zvonoBotSettings: {
        id: config.marketplaceModulesIds[config.buildType].zvonoBotSettings,
        card: {},
        page: {
            headerCard: {
                icon: "📡",
                badge: { color: '#525AD5', bcg: '#EEF0FF', text: null },
                tags: 'Услуга',
                title: null,
                shortDescription: "Запустим аудиорассылку по базе клиентов: подготовим базу и аудио, настроим сценарий звонка и передадим инструкции команде.",
                extraInfoHtml: "от <strong>Data Heroes</strong>",
            },
            aboutCard: {
                description: "Аудиорассылки DH — обзвон по базе потерянных клиентов. Помогаем обратиться к тем, кто не реагирует на сообщения, и передаём тёплые обращения администратору.",
                secondDescription: "Подготовим базу и аудиосообщение, настроим расписание, повторные попытки и сценарий звонка. Команда получит инструкцию по работе с обращениями и отчёт по итогам. Подписка включает 1 цикл в месяц — 3 запуска обзвона.",
                facts: [
                    { title: "3 дня", description: "до готового запуска" },
                    { title: "3 запуска", description: "обзвона в каждом месяце" },
                    { title: "Под ключ", description: "база, аудио, сценарий и инструкция" },
                    { title: "От 7 000 ₽/мес", description: "при подписке на 12 месяцев" },
                ],
            },
            skillsCards: [
                {
                    icon: "📞",
                    title: "Сценарий аудиорассылки",
                    description: "Нажал 1 — тёплый лид администратору. Нажал 2 — больше не звоним. Не ответил — ещё 2 попытки, затем стоп.",
                },
                {
                    icon: "🎯",
                    title: "Подготовка базы",
                    description: "Сформируем и проверим аудиторию для аудиорассылки по базе потерянных клиентов.",
                },
                {
                    icon: "🔊",
                    title: "Аудиосообщение под бизнес",
                    description: "Подготовим текст, поможем с записью и проверим аудиоролик перед запуском.",
                },
                {
                    icon: "📋",
                    title: "Лиды администратору",
                    description: "Передадим тёплые обращения с инструкцией, как обрабатывать звонки и фиксировать результат.",
                },
                {
                    icon: "🔁",
                    title: "Повторные попытки",
                    description: "Настроим расписание и повторы для тех, кто не ответил. Отказавшимся больше не звоним.",
                },
                {
                    icon: "📊",
                    title: "Аналитика по итогам",
                    description: "Подготовим отчёт с результатами проведённого обзвона по итогам цикла.",
                },
            ],
            otherIntegrations: [

                {
                    id: config.marketplaceModulesIds[config.buildType].crmSettings,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].beautyAutopilot,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].crmTO,
                    price: null,
                    rating: null,
                },
                {
                    id: config.marketplaceModulesIds[config.buildType].salesTraining,
                    price: null,
                    rating: null,
                },
            ],
            connectCard: {
                price: null,
                pricing: {
                    type: 'tiers',
                    header: "Цена за месяц по сроку",
                    unit: "/мес",
                    flagLast: true,
                    rows: [{ term: "1 месяц", price: "10 000 ₽" },
                        { term: "3 месяца", price: "9 000 ₽" },
                        { term: "6 месяцев", price: "8 000 ₽" },
                        { term: "12 месяцев", price: "7 000 ₽" }],
                    note: "+ телефония по фактическому расходу.\nОриентир — от 3 000 ₽ за 3 обзвона.",
                },
                facts: [
                    "3 запуска обзвона в месяц",
                    "Подготовка аудиосообщения",
                    "Подготовка базы клиентов",
                    "Настройка сценария звонков",
                    "Передача тёплых лидов",
                    "Инструкция для администратора",
                    "Отчёт по итогам обзвона",
                ],
                dhOfficial: true,
            },
        },
    },
}

export function resolveUxCardBackground(bcg: string): Record<string, string> {
    const value = bcg.trim()
    if (!value) return {}

    if (/gradient|url\(/i.test(value)) {
        return { background: value }
    }

    return { backgroundColor: value }
}

export function getUxModuleSettings(integrationId: string | number): UxModuleSettings | null {
    const normalizedId = String(integrationId)

    for (const settings of Object.values(uxSettings)) {
        if (settings?.id && String(settings.id) === normalizedId) {
            return settings
        }
    }

    return null
}

export function getUxPageSettings(integrationId: string | number): UxPageSettings | null {
    const normalizedId = String(integrationId)

    for (const settings of Object.values(uxSettings)) {
        if (!settings?.id || String(settings.id) !== normalizedId || !settings.page) {
            continue
        }

        if (!Object.keys(settings.page).length) {
            return null
        }

        return settings.page
    }

    return null
}

export function getUxCardSettings(
    integrationId: string | number,
): Partial<UxCardSettings> | null {
    const normalizedId = String(integrationId)

    for (const settings of Object.values(uxSettings)) {
        if (!settings?.id || String(settings.id) !== normalizedId || !settings.card) {
            continue
        }

        return settings.card
    }

    return null
}

export function formatUxCardPriceParts(price: UxCardSettings['price']) {
    const amount = `От ${price.price} ₽`

    if (price.period === 'month') {
        return { amount, suffix: '/мес' as const }
    }

    if (price.period === 'once') {
        return { amount, suffix: 'разово' as const }
    }

    return { amount, suffix: null as null }
}

export function formatUxCardPrice(price: UxCardSettings['price']): string {
    const { amount, suffix } = formatUxCardPriceParts(price)
    return suffix ? `${amount} ${suffix}` : amount
}
